import PolicyLayout, { MetaBox, H2, H3, Ul, SimpleTable } from '../components/PolicyLayout';

const TermsPage = () => (
  <PolicyLayout
    title="Terms & Conditions"
    subtitle="mybook.rent — India's First Peer-to-Peer Book Rental Platform"
    updated="Last Updated: September 2026"
    testId="terms-page"
  >
    <MetaBox
      rows={[
        ['Platform', 'mybook.rent'],
        ['Legal Entity', 'BOOKONRENT'],
        ['Registered Address', '312, Incor Opulence, Gunjur Palya Road, Bengaluru, Karnataka – 560087'],
        ['Contact', 'support@mybook.rent | +91 86607 85749'],
        ['Governing Law', 'Laws of India | Jurisdiction: Bengaluru, Karnataka'],
      ]}
    />

    <p>
      Thank you for visiting mybook.rent. For the purpose of these Terms and Conditions (&ldquo;Terms&rdquo;),
      the terms &ldquo;we&rdquo;, &ldquo;us&rdquo;, and &ldquo;our&rdquo; refer to mybook.rent, operated by BOOKONRENT,
      whose registered and operational office is at 312, Incor Opulence, Gunjur Palya Road, Bengaluru,
      Karnataka – 560087. The terms &ldquo;you&rdquo;, &ldquo;your&rdquo;, &ldquo;user&rdquo;, &ldquo;visitor&rdquo;,
      &ldquo;borrower&rdquo;, or &ldquo;lender&rdquo; refer to any natural or legal person visiting or using our Platform.
    </p>
    <p>
      By accessing or using the mybook.rent Platform — whether as a borrower, a lender, or a general visitor —
      you agree to be bound by these Terms. If you do not agree with any part of these Terms, please do not use the Platform.
    </p>
    <p>
      mybook.rent is a peer-to-peer book rental marketplace. We connect individual book lenders and borrowers.
      The books listed on our platform belong to individual community members — not to mybook.rent. Please read
      these Terms carefully as they define the rights and obligations of all parties on our platform.
    </p>

    <H2>1. Platform Eligibility</H2>
    <p>By using our Platform, you confirm that:</p>
    <Ul>
      <li>You are at least 18 years of age, or have the consent of a parent or legal guardian to use this Platform.</li>
      <li>You are legally capable of entering into a binding agreement under applicable Indian law.</li>
      <li>You will use the Platform only for lawful purposes and in accordance with these Terms.</li>
      <li>The information you provide on the Platform — including your name, address, contact details, and payment information — is accurate, current, and complete.</li>
    </Ul>

    <H2>2. The Platform — How mybook.rent Works</H2>
    <p>
      mybook.rent is a technology-driven, peer-to-peer book rental marketplace. We provide the platform, tools,
      and infrastructure that enable:
    </p>
    <Ul>
      <li><strong>Lenders</strong> — community members who list their personal book collections for rent — to make their books available to borrowers.</li>
      <li><strong>Borrowers</strong> — readers seeking affordable access to books — to discover, book, and collect books from lenders in their neighbourhood.</li>
    </Ul>
    <p>
      mybook.rent is the facilitating platform — we are not the owner, seller, or custodian of any book listed
      on the platform. The rental relationship is between the lender and the borrower. mybook.rent&rsquo;s role is
      to facilitate and govern that relationship through these Terms and our platform policies.
    </p>

    <H2>3. User Accounts</H2>
    <Ul>
      <li>You are responsible for maintaining the confidentiality of your account credentials — username, password, and OTP — and for all activities that occur under your account.</li>
      <li>You must notify us immediately at support@mybook.rent if you suspect any unauthorised access to your account.</li>
      <li>You may not create more than one account per individual. Duplicate accounts may be suspended without notice.</li>
      <li>mybook.rent reserves the right to refuse registration, suspend, or permanently terminate any account at its sole discretion — including where we believe the account is being used in violation of these Terms.</li>
      <li>You are responsible for keeping your contact details and address up to date on your account profile.</li>
    </Ul>

    <H2>4. Lender Obligations</H2>
    <p>If you list books on mybook.rent as a lender, you agree to the following:</p>
    <Ul>
      <li>You are the rightful owner of all books you list on the platform, or have the legal right to lend them.</li>
      <li>All listing information — title, author, edition, condition, and rental price — must be accurate and not misleading.</li>
      <li>The physical condition of the book at the time of pickup must match the condition described in the listing. Significant misrepresentation may result in listing suspension.</li>
      <li>You will make the book available for pickup at the agreed location and time once a rental is confirmed.</li>
      <li>You will conduct a fair and honest condition check with the borrower at both pickup and return.</li>
      <li>You will acknowledge receipt of returned books on the platform within 24 hours of physical return.</li>
      <li>You will not cancel confirmed bookings without good reason. Repeated cancellations may result in listing suspension.</li>
      <li>You will resolve disputes with borrowers in good faith, using mybook.rent&rsquo;s dispute resolution process where needed.</li>
    </Ul>

    <H2>5. Borrower Obligations</H2>
    <p>If you borrow books through mybook.rent, you agree to the following:</p>
    <Ul>
      <li>You will collect the booked book from the agreed location at the agreed time.</li>
      <li>You will treat all borrowed books with care and return them in the same condition in which you received them.</li>
      <li>You will return the book by the agreed return date. Late returns attract late fees as per our Returns &amp; Refunds Policy.</li>
      <li>If a book is damaged while in your possession, you are responsible for the full replacement cost.</li>
      <li>If a book is lost or not returned within 7 days of the due date, you are liable for the replacement cost plus a penalty fee as per our Returns &amp; Refunds Policy.</li>
      <li>You will conduct a condition check with the lender at pickup — photographs are strongly recommended to protect both parties.</li>
      <li>You will not reproduce, photocopy, scan, or digitise any book borrowed through the platform.</li>
    </Ul>

    <H2>6. Book Pickup &amp; Self-Collection</H2>
    <p>mybook.rent currently operates on a self-pickup model. This means:</p>
    <Ul>
      <li>All book pickups and returns are made directly between the borrower and lender at the lender&rsquo;s designated pickup location.</li>
      <li>mybook.rent does not provide delivery or logistics services at this stage. There is no shipping involved.</li>
      <li>The pickup location, available pickup times, and any specific instructions are specified by the lender in their listing.</li>
      <li>Borrowers must respect the lender&rsquo;s pickup preferences and not show up unannounced at any other time or location.</li>
      <li>mybook.rent is not responsible for any inconvenience arising from the borrower&rsquo;s failure to collect a confirmed booking within the agreed timeframe.</li>
      <li>A delivery option may be introduced in a future phase of the platform. Any such service will be governed by a separate Shipping &amp; Delivery Policy published at that time.</li>
    </Ul>

    <H2>7. Rental Fees &amp; Payments</H2>
    <Ul>
      <li>Rental fees are set by individual lenders and displayed clearly on each listing. mybook.rent charges a platform commission (5%) on each successful rental transaction. Additionally, GST of 18% applies on the total order value.</li>
      <li>All payments on the platform are processed through secure third-party payment gateways. mybook.rent does not store your full payment card or banking details.</li>
      <li>You agree to provide accurate, current, and complete payment information for all transactions on the platform.</li>
      <li>All prices displayed are in Indian Rupees (₹) inclusive of applicable taxes.</li>
      <li>mybook.rent reserves the right to modify its commission structure or introduce new fee types at any time, with reasonable prior notice to users.</li>
    </Ul>

    <H2>8. Cancellations &amp; Refunds</H2>
    <p>
      Cancellations, returns, and refunds are governed by mybook.rent&rsquo;s Returns, Refunds &amp; Cancellation Policy,
      which is incorporated into these Terms by reference and available on the Platform. Key highlights:
    </p>
    <Ul>
      <li>Cancellations are permitted until pickup time — full refund applies.</li>
      <li>After pickup time, cancellation is closed and there is no refund.</li>
      <li>Early returns within 24 hours of pickup — 50% refund.</li>
      <li>Damaged or lost books — borrower is liable for replacement cost and applicable penalties.</li>
    </Ul>
    <p>Please refer to the full Returns, Refunds &amp; Cancellation Policy on the Platform for complete details.</p>

    <H2>9. Intellectual Property</H2>
    <Ul>
      <li>All content on the mybook.rent Platform — including text, graphics, logos, icons, images, software, data compilations, and the overall design — is the property of BOOKONRENT or its licensed content providers and is protected by applicable intellectual property laws of India.</li>
      <li>You are granted a limited, non-exclusive, non-transferable, and revocable licence to access and use the Platform for personal, non-commercial purposes only.</li>
      <li>You may not reproduce, duplicate, copy, sell, resell, modify, distribute, or otherwise exploit any content from the Platform without the express prior written consent of mybook.rent.</li>
      <li>The mybook.rent brand name, logo, and tagline are trademarks of BOOKONRENT. Unauthorised use of these marks is strictly prohibited.</li>
      <li>Books listed on the platform are the intellectual property of their respective authors and publishers. Borrowing a book through mybook.rent does not grant you any rights to reproduce, copy, or digitise its contents.</li>
    </Ul>

    <H2>10. User Conduct</H2>
    <p>By using the Platform, you agree not to:</p>
    <Ul>
      <li>Upload, post, or transmit any content that is unlawful, harmful, threatening, abusive, harassing, defamatory, obscene, or otherwise objectionable.</li>
      <li>Use the Platform for any fraudulent, deceptive, or misleading purpose — including creating false listings, misrepresenting book conditions, or submitting false damage claims.</li>
      <li>Attempt to contact other users outside the platform to conduct rental transactions — bypassing the platform&rsquo;s payment and governance systems.</li>
      <li>Interfere with or disrupt the Platform&rsquo;s servers, security systems, or any networks connected to the Platform.</li>
      <li>Use automated tools, bots, or scrapers to access, extract, or interact with any part of the Platform without prior written consent.</li>
      <li>Impersonate any person or entity, or misrepresent your affiliation with any person or entity.</li>
      <li>Reproduce, scan, photocopy, or digitise any book borrowed through the Platform.</li>
    </Ul>

    <H2>11. Platform Availability &amp; Changes</H2>
    <Ul>
      <li>mybook.rent strives to maintain Platform availability at all times but does not guarantee uninterrupted access. Scheduled or unscheduled maintenance may temporarily affect availability.</li>
      <li>We reserve the right to modify, update, suspend, or discontinue any feature or aspect of the Platform at any time without prior notice.</li>
      <li>We are not liable for any loss or inconvenience caused by temporary unavailability of the Platform.</li>
    </Ul>

    <H2>12. Limitation of Liability</H2>
    <p>To the maximum extent permitted by applicable law, mybook.rent and BOOKONRENT shall not be liable for:</p>
    <Ul>
      <li>Any direct, indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the Platform.</li>
      <li>The quality, condition, accuracy of description, or fitness of any book listed by a lender on the Platform — lenders are solely responsible for their listings.</li>
      <li>Any loss, damage, or theft of a book during a rental transaction between a lender and borrower.</li>
      <li>Any dispute arising between a lender and borrower that cannot be resolved through the Platform&rsquo;s dispute resolution process.</li>
      <li>Any harm arising from third-party services, payment gateways, or external links accessed through the Platform.</li>
    </Ul>
    <p>
      Our total liability to you in connection with any claim arising out of or relating to the Platform shall not
      exceed the total amount paid by you to mybook.rent in the 3 months preceding the claim.
    </p>

    <H2>13. Indemnification</H2>
    <p>
      You agree to indemnify, defend, and hold harmless mybook.rent, BOOKONRENT, its officers, directors, contractors,
      and agents from and against all claims, losses, liabilities, damages, costs, and expenses — including reasonable
      legal fees — arising from or relating to:
    </p>
    <Ul>
      <li>Your use of the Platform in violation of these Terms.</li>
      <li>Your listings, rental transactions, or interactions with other users on the Platform.</li>
      <li>Any content you post, upload, or transmit through the Platform.</li>
      <li>Your violation of any applicable law or third-party right.</li>
    </Ul>

    <H2>14. Privacy &amp; Data Protection</H2>
    <p>
      Your use of the Platform is also governed by our Privacy Policy, which is incorporated into these Terms by
      reference and available on the Platform. By using the Platform, you consent to the collection, use, and
      processing of your personal data as described in the Privacy Policy, in accordance with the Digital Personal
      Data Protection (DPDP) Act, 2023 and other applicable Indian law.
    </p>

    <H2>15. Termination</H2>
    <Ul>
      <li>mybook.rent may, at its sole discretion, suspend or permanently terminate your access to the Platform at any time — including where we reasonably believe you have violated these Terms, engaged in fraudulent activity, or caused harm to other users.</li>
      <li>You may terminate your account at any time by contacting support@mybook.rent. Termination does not affect any rights or obligations that arose prior to the termination date.</li>
      <li>Upon termination, all licences granted to you under these Terms will immediately cease. Any outstanding rental obligations or financial liabilities must be settled prior to account closure.</li>
    </Ul>

    <H2>16. Changes to These Terms</H2>
    <p>
      mybook.rent reserves the right to modify or update these Terms at any time. When we make material changes,
      we will update the &ldquo;Last Updated&rdquo; date at the top of this page and, where appropriate, notify
      registered users by email. Your continued use of the Platform following the posting of revised Terms
      constitutes your acceptance of those changes.
    </p>
    <p>We encourage you to review these Terms periodically to stay informed of any updates.</p>

    <H2>17. Governing Law &amp; Jurisdiction</H2>
    <p>
      These Terms shall be governed by and construed in accordance with the laws of India. Any dispute arising out
      of or in connection with these Terms or your use of the Platform shall be subject to the exclusive jurisdiction
      of the courts of Bengaluru, Karnataka, India.
    </p>
    <p>
      Before initiating any legal proceedings, both parties agree to make a genuine effort to resolve disputes
      amicably through direct discussion or mediation.
    </p>

    <H2>18. Security Deposit</H2>
    <H3>18.1 What is the Security Deposit?</H3>
    <p>
      The Security Deposit is a fully refundable amount collected by mybook.rent from borrowers to protect lenders
      against the risk of books not being returned, being returned damaged, or being lost. It is not a fee — it is
      a safeguard that is returned to the borrower when their account is closed in good standing.
    </p>
    <p>
      The Security Deposit reflects mybook.rent&rsquo;s commitment to building a trusted, community-driven platform
      where lenders can share their books with confidence.
    </p>

    <H3>18.2 When is the Security Deposit Collected?</H3>
    <Ul>
      <li>The Security Deposit is collected at the time of the borrower&rsquo;s first rental transaction on the platform — not at account registration.</li>
      <li>The deposit amount is determined as 60% of the MRP of the book being rented, collected at the time of the borrower&rsquo;s first rental transaction.</li>
    </Ul>

    <H3>18.3 Refund of Security Deposit</H3>
    <p>
      The Security Deposit is fully refundable and will be returned to the borrower&rsquo;s original payment method
      upon account closure, provided:
    </p>
    <Ul>
      <li>(a) All borrowed books have been returned to their respective lenders in acceptable condition.</li>
      <li>(b) No outstanding damage fees, replacement costs, or penalty fees are payable.</li>
      <li>(c) A formal account closure request has been submitted to support@mybook.rent.</li>
    </Ul>
    <p>
      Refund processing will be completed within 7–10 business days of account closure confirmation. Any outstanding
      amounts owed by the borrower will be deducted from the Security Deposit before the balance is refunded.
    </p>

    <H3>18.4 Deductions from Security Deposit</H3>
    <p>mybook.rent may deduct amounts from the Security Deposit in the following circumstances:</p>
    <Ul>
      <li>A borrowed book is returned damaged — the replacement cost of the book will be deducted.</li>
      <li>A borrowed book is not returned within 7 days of the due date — the full replacement cost plus a penalty fee (₹100 or 50% of total rental fee, whichever is higher) will be deducted.</li>
      <li>Outstanding rental fees or late fees remain unpaid at the time of account closure.</li>
    </Ul>

    <H3>18.5 Account Suspension for Deposit Depletion</H3>
    <p>If a deduction is made from the Security Deposit for any reason:</p>
    <Ul>
      <li>The borrower&rsquo;s account will be immediately suspended and all active rental bookings will be paused.</li>
      <li>The borrower will be notified by email and WhatsApp with details of the deduction and the amount required to reinstate the deposit to its original level.</li>
      <li>The borrower must top up the Security Deposit to its original amount within 7 days to reactivate their account.</li>
      <li>If the deposit is not topped up within 7 days, the account will remain suspended. Repeated non-payment may result in permanent account termination.</li>
      <li>During suspension, no new rental bookings may be made. The borrower retains access to their account history and can view past transactions.</li>
    </Ul>
    <p>
      mybook.rent will always communicate deductions transparently and in writing before acting. We are committed to
      fair and honest handling of all security deposit matters.
    </p>

    <H2>19. Lost or Unreturned Premium &amp; High-Value Books</H2>
    <p>
      In addition to the standard Lost Book Policy in the Returns, Refunds &amp; Cancellation Policy, the following
      conditions apply specifically to Premium Books (MRP &gt; ₹1,000) and Listed Books (MRP ₹500–₹1,000):
    </p>
    <Ul>
      <li>The borrower is liable for the full replacement cost of the book at its current market price (new copy) at the time the loss is confirmed.</li>
      <li>mybook.rent will deduct the available Security Deposit balance immediately upon confirming the loss.</li>
      <li>If the replacement cost exceeds the Security Deposit balance, the shortfall is an outstanding debt owed by the borrower to mybook.rent and the lender.</li>
      <li>mybook.rent will make two written payment requests (7 days apart) for the outstanding amount before initiating further recovery action.</li>
      <li>Persistent non-payment may result in permanent account ban and, in cases of significant value, referral to appropriate legal mechanisms.</li>
      <li>The lender will be compensated from the Security Deposit and, where available, from any recovery proceeds, as promptly as possible.</li>
    </Ul>

    <H2>20. Lender Acknowledgement for Listed &amp; Premium Books</H2>
    <p>
      Lenders who list books categorised as Listed Books (MRP ₹500–₹1,000) or Premium Books (MRP &gt; ₹1,000)
      acknowledge and agree to the following:
    </p>
    <Ul>
      <li>They are voluntarily making their books available for rental through the platform&rsquo;s access tier framework.</li>
      <li>Borrower access to their books is protected by the Security Deposit as described in this policy.</li>
      <li>mybook.rent&rsquo;s liability in the event of loss or damage is limited to facilitating the Security Deposit deduction and recovery process — mybook.rent does not guarantee full replacement value in all circumstances.</li>
      <li>For extremely rare, first edition, signed, or irreplaceable books, lenders are strongly advised to refrain from listing on the platform, as the Security Deposit framework may not fully cover such items&rsquo; true value.</li>
    </Ul>

    <H2>21. Contact Us</H2>
    <p>If you have any questions, concerns, or feedback regarding these Terms, please contact us:</p>
    <MetaBox
      rows={[
        ['Email', 'support@mybook.rent'],
        ['WhatsApp / Phone', '+91 86607 85749'],
        ['Address', '312, Incor Opulence, Gunjur Palya Road, Bengaluru, Karnataka – 560087'],
        ['Response Time', 'Within 2 business days'],
      ]}
    />
  </PolicyLayout>
);

export default TermsPage;
